import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { ArrowLeft, ArrowRight, CalendarBlank, Timer, Barbell } from "@phosphor-icons/react";
import { getFocusMeta } from "@/utils/focusMeta";
import { HighlightBadge } from "@/components/HighlightBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingDetail() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regForm, setRegForm] = useState({ full_name: "", rut: "", phone: "", age: "", gender: "masculino", email: "", city: "" });
  const [redirectInfo, setRedirectInfo] = useState(null); // { url, amount, title }
  const { t, lang } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/training-plans/${planId}`).then(r => setPlan(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [planId]);

  const levelColors = { principiante: "text-green-400 border-green-400/30", intermedio: "text-yellow-400 border-yellow-400/30", avanzado: "text-red-400 border-red-400/30" };

  if (loading) return <main className="pt-20 min-h-screen flex items-center justify-center"><p className="text-gray-400">{t("loading")}</p></main>;
  if (!plan) return <main className="pt-20 min-h-screen flex items-center justify-center"><p className="text-gray-400">Plan no encontrado</p></main>;

  const sections = plan.content_sections || [];

  const handleRegister = async () => {
    if (!regForm.full_name || !regForm.email || !regForm.phone) {
      toast.error(lang === "en" ? "Fill required fields" : "Completa los campos obligatorios"); return;
    }
    setRegLoading(true);
    try {
      const { data } = await axios.post(`${API}/event-registrations`, {
        event_id: plan.id, event_title: plan.title, distance_label: plan.focus,
        distance_price: plan.price, full_name: regForm.full_name, rut: regForm.rut || "N/A",
        phone: regForm.phone, age: Number(regForm.age) || 0, gender: regForm.gender,
        category: "entrenamiento", email: regForm.email, city: regForm.city || "", club: "", shirt_size: "N/A"
      });
      if (data.checkout_url) {
        setRegOpen(false);
        setRedirectInfo({ url: data.checkout_url, amount: data.final_price ?? plan.price, title: plan.title });
        return;
      }
      toast.success(lang === "en" ? "Registration successful!" : "Inscripcion exitosa!");
      setRegOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setRegLoading(false); }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="training-detail-page">
      {/* Hero */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img src={plan.image_url} alt={plan.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <HighlightBadge text={plan.badge_text} color={plan.badge_color} className="top-4 left-4 sm:top-6 sm:left-6 text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-1.5" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <Link to="/entrenamientos" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#FF4D00] transition-colors mb-4">
            <ArrowLeft size={14} /> {lang === "en" ? "Back to Training" : "Volver a Entrenamientos"}
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={`text-xs ${levelColors[plan.level] || ""}`}>{plan.level}</Badge>
            {(() => { const { Icon: FocusIcon, label: focusLabel } = getFocusMeta(plan.focus); return (
              <Badge variant="outline" className="text-xs text-gray-400 border-white/10 inline-flex items-center gap-1" data-testid="training-detail-focus"><FocusIcon size={12} weight="bold" />{focusLabel}</Badge>
            ); })()}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black uppercase font-['Cabinet_Grotesk'] tracking-tight">{plan.title}</h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        {/* Stats */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="bg-[#121212] border border-white/5 px-5 py-3">
            <p className="text-xs text-gray-500"><CalendarBlank size={12} className="inline mr-1" />{lang === "en" ? "Duration" : "Duracion"}</p>
            <p className="text-lg font-bold font-['Cabinet_Grotesk']">{plan.duration_weeks} {lang === "en" ? "weeks" : "semanas"}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 px-5 py-3">
            <p className="text-xs text-gray-500"><Timer size={12} className="inline mr-1" />{lang === "en" ? "Frequency" : "Frecuencia"}</p>
            <p className="text-lg font-bold font-['Cabinet_Grotesk']">{plan.sessions_per_week}x/{lang === "en" ? "week" : "semana"}</p>
          </div>
          <div className="bg-[#121212] border border-white/5 px-5 py-3">
            <p className="text-xs text-gray-500">{t("inscription")}</p>
            <p className="text-lg font-bold font-['Cabinet_Grotesk'] text-[#FF4D00]">{plan.price === 0 ? (lang === "en" ? "Free" : "Gratis") : formatPrice(plan.price)}</p>
          </div>
        </div>

        {/* Description */}
        <div className="bg-[#121212] border border-white/5 p-6 mb-8">
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{plan.description}</p>
        </div>

        {/* Quick consultation form */}
        <ConsultationForm plan={plan} lang={lang} />

        {/* Content Sections */}
        {sections.length > 0 && (
          <div className="space-y-6 mb-8">
            {sections.map((section, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
                {section.type === "text" && (
                  <div className="bg-[#121212] border border-white/5 p-6">
                    {section.title && <h3 className="text-lg font-semibold font-['Cabinet_Grotesk'] mb-3">{section.title}</h3>}
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{section.content}</p>
                  </div>
                )}
                {section.type === "image" && (
                  <div className="overflow-hidden border border-white/5">
                    <img src={section.content} alt={section.title || ""} className="w-full h-auto object-cover" />
                    {section.title && <p className="text-xs text-gray-500 p-3 bg-[#121212]">{section.title}</p>}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Coaches del plan */}
        {(plan.coaches || []).length > 0 && (
          <div className="mb-12" data-testid="training-coaches">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-6">{lang === "en" ? "Coaches & specialists" : "Profesionales del programa"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {plan.coaches.map((c, i) => (
                <div key={i} className="bg-[#121212] border border-white/5 p-5 flex gap-4 items-start" data-testid={`coach-card-${i}`}>
                  <div className="w-20 h-20 bg-[#1A1A1A] border border-white/10 flex-shrink-0 overflow-hidden rounded-full">
                    {c.photo_url && <img src={c.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${c.photo_url}` : c.photo_url} alt={c.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold font-['Cabinet_Grotesk']">{c.name}</h3>
                    {c.role && <p className="text-xs text-[#FF4D00] uppercase tracking-wider mt-0.5">{c.role}</p>}
                    {c.description && <p className="text-sm text-gray-400 mt-2 leading-relaxed whitespace-pre-wrap">{c.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Register CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between bg-[#121212] border border-[#FF4D00]/20 p-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">{plan.title}</p>
            <span className="text-3xl font-black text-[#FF4D00] font-['Cabinet_Grotesk']">{plan.price === 0 ? (lang === "en" ? "Free" : "Gratis") : formatPrice(plan.price)}</span>
          </div>
          <Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-14 px-8 text-base mt-4 sm:mt-0" onClick={() => setRegOpen(true)} data-testid="training-register-btn">
            {t("register")} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Registration Dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="training-reg-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cabinet_Grotesk'] text-xl">{t("inscription")}</DialogTitle>
            <DialogDescription>{plan.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-400">{lang === "en" ? "Full Name" : "Nombre Completo"} *</Label>
              <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.full_name} onChange={e => setRegForm(p => ({ ...p, full_name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Email *</Label>
                <Input type="email" className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div><Label className="text-xs text-gray-400">{lang === "en" ? "Phone" : "Celular"} *</Label>
                <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">{lang === "en" ? "Age" : "Edad"}</Label>
                <Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.age} onChange={e => setRegForm(p => ({ ...p, age: e.target.value }))} /></div>
              <div><Label className="text-xs text-gray-400">{lang === "en" ? "City" : "Ciudad"}</Label>
                <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={regForm.city} onChange={e => setRegForm(p => ({ ...p, city: e.target.value }))} /></div>
            </div>
            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between mb-3">
                <span className="text-sm text-gray-400">{plan.title}</span>
                <span className="font-bold text-[#FF4D00]">{plan.price === 0 ? "Gratis" : formatPrice(plan.price)}</span>
              </div>
              <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12" disabled={regLoading} onClick={handleRegister}>
                {regLoading ? "..." : (lang === "en" ? "Confirm" : "Confirmar Inscripcion")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal confirmación pre-redirect a MercadoPago */}
      <Dialog open={!!redirectInfo} onOpenChange={(v) => { if (!v) setRedirectInfo(null); }}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="mp-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cabinet_Grotesk'] uppercase tracking-tight">{lang === "en" ? "Confirm payment" : "Confirmar pago"}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {lang === "en" ? "You're about to be redirected to MercadoPago to complete your purchase." : "Estas por ser redirigido a MercadoPago para completar tu pago."}
            </DialogDescription>
          </DialogHeader>
          {redirectInfo && (
            <div className="space-y-4 pt-2">
              <div className="bg-[#1A1A1A] border border-white/10 p-4">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{lang === "en" ? "Plan" : "Plan"}</p>
                <p className="text-base font-semibold mb-3">{redirectInfo.title}</p>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{lang === "en" ? "Total" : "Total"}</p>
                <p className="text-2xl font-bold text-[#FF4D00]">{formatPrice(redirectInfo.amount)}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-white/15 text-white hover:bg-white/5" onClick={() => setRedirectInfo(null)} data-testid="mp-cancel-btn">
                  {lang === "en" ? "Cancel" : "Cancelar"}
                </Button>
                <Button className="flex-1 bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" onClick={() => { window.location.href = redirectInfo.url; }} data-testid="mp-go-btn">
                  {lang === "en" ? "Pay now" : "Pagar ahora"} <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 text-center">{lang === "en" ? "Secure payment via MercadoPago" : "Pago seguro vía MercadoPago"}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function ConsultationForm({ plan, lang }) {
  const [form, setForm] = useState({ full_name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(lang === "en" ? "Fill all fields" : "Completa todos los campos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/training-consultations`, {
        plan_id: plan.id,
        plan_title: plan.title,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim()
      });
      toast.success(lang === "en" ? "Inquiry sent! We'll contact you soon." : "Consulta enviada! Te contactaremos pronto.");
      setSent(true);
      setForm({ full_name: "", phone: "", message: "" });
      if (data.whatsapp_url) {
        // Optional: open WhatsApp tab so cliente puede confirmar directo si quiere
        try { window.open(data.whatsapp_url, "_blank"); } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "en" ? "Error" : "Error al enviar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="training-consultation-form">
      <div className="mb-4">
        <h3 className="text-base sm:text-lg font-bold font-['Cabinet_Grotesk'] uppercase tracking-tight">{lang === "en" ? "Inquiries" : "Consultas"}</h3>
      </div>
      {sent ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-4">
          <p className="text-sm text-emerald-300 font-semibold">✓ {lang === "en" ? "Inquiry received." : "Consulta recibida."}</p>
          <p className="text-xs text-gray-300 mt-1">{lang === "en" ? "Thanks for reaching out — we'll contact you shortly." : "Gracias por escribirnos — nos pondremos en contacto contigo pronto."}</p>
          <button onClick={() => setSent(false)} className="text-xs text-[#FF4D00] hover:underline mt-2" data-testid="consultation-send-another">
            {lang === "en" ? "Send another" : "Enviar otra consulta"}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-400">{lang === "en" ? "Full name" : "Nombre"} *</Label>
              <Input
                className="bg-[#1A1A1A] border-white/10 mt-1"
                value={form.full_name}
                onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                placeholder={lang === "en" ? "Your name" : "Tu nombre"}
                data-testid="consultation-name"
                required
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">{lang === "en" ? "Phone" : "Telefono"} *</Label>
              <Input
                className="bg-[#1A1A1A] border-white/10 mt-1"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+56 9 1234 5678"
                data-testid="consultation-phone"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-400">{lang === "en" ? "Your inquiry" : "Tu consulta"} *</Label>
            <Textarea
              className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[100px]"
              value={form.message}
              onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder={lang === "en" ? "Tell us what you'd like to know about this plan..." : "Cuentanos que te gustaria saber sobre este plan..."}
              data-testid="consultation-message"
              required
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={loading} className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" data-testid="consultation-submit">
              {loading ? "..." : (lang === "en" ? "Send inquiry" : "Enviar consulta")} <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
